import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  ReactNode,
} from "react";

export type ThemeMode = "light" | "dark" | "auto";

const THEME_MODE_KEY = "theme-mode";

// Event mechanism to sync standalone functions with React Context
type ThemeChangeListener = (mode: ThemeMode) => void;
const listeners = new Set<ThemeChangeListener>();

function notifyThemeChange() {
  const mode = getCurrentThemeMode();
  listeners.forEach((listener) => listener(mode));
}

function subscribeToThemeChange(listener: ThemeChangeListener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function updateTheme(darkPreferred: boolean): void {
  if (darkPreferred) {
    document.body.classList.add("dark");
  } else {
    document.body.classList.remove("dark");
  }
}

let currentMediaQuery: MediaQueryList | null = null;

// Apply theme immediately on module load to prevent flash
if (typeof window !== "undefined" && typeof document !== "undefined") {
  const storedMode = typeof localStorage !== "undefined" ? localStorage.getItem(THEME_MODE_KEY) : null;
  
  if (storedMode === "light") {
    document.body.classList.remove("dark");
  } else if (storedMode === "auto") {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    currentMediaQuery = mediaQuery;
    updateTheme(mediaQuery.matches);
    mediaQuery.onchange = (e: MediaQueryListEvent) => {
      updateTheme(e.matches);
    };
  } else {
    // Default to dark mode
    document.body.classList.add("dark");
  }
}

/**
 * Switch to dark mode by adding the "dark" class to document.body.
 */
export function switchToDarkMode(): void {
  // Clear any auto mode listener if present.
  if (currentMediaQuery) {
    currentMediaQuery.onchange = null;
    currentMediaQuery = null;
  }
  document.body.classList.add("dark");
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(THEME_MODE_KEY, "dark");
  }
  notifyThemeChange();
}

/**
 * Switch to light mode by removing the "dark" class from document.body.
 */
export function switchToLightMode(): void {
  // Clear any auto mode listener if present.
  if (currentMediaQuery) {
    currentMediaQuery.onchange = null;
    currentMediaQuery = null;
  }
  document.body.classList.remove("dark");
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(THEME_MODE_KEY, "light");
  }
  notifyThemeChange();
}

/**
 * Switch to auto mode. This function immediately applies the user's color scheme preference
 * and listens for system preference changes to update the theme automatically.
 * It uses the onchange property instead of addEventListener to avoid TypeScript issues.
 */
export function switchToAutoMode(): void {
  if (currentMediaQuery) {
    currentMediaQuery.onchange = null;
  }
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(THEME_MODE_KEY, "auto");
  }
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  mediaQuery.onchange = (e: MediaQueryListEvent) => {
    updateTheme(e.matches);
  };
  currentMediaQuery = mediaQuery;
  updateTheme(mediaQuery.matches);
  notifyThemeChange();
}

/**
 * Returns the current theme mode:
 * - reads from localStorage if available
 * - fallback to "auto" if auto mode is enabled,
 * - fallback to dark mode otherwise.
 */
export function getCurrentThemeMode(): ThemeMode {
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(THEME_MODE_KEY);
    if (stored === "auto") return "auto";
    if (stored === "light") return "light";
    if (stored === "dark") return "dark";
    // default to dark if not stored
    return "dark";
  }

  if (currentMediaQuery) {
    return "auto";
  }
  return document.body.classList.contains("dark") ? "dark" : "light";
}

// -- React Context & Provider --

interface ThemeModeContextValue {
  mode: ThemeMode;
  switchToDarkMode: () => void;
  switchToLightMode: () => void;
  switchToAutoMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(() => getCurrentThemeMode());

  useEffect(() => {
    // Subscribe to changes triggered by standalone functions
    const unsubscribe = subscribeToThemeChange((newMode) => {
      setMode(newMode);
    });
    return unsubscribe;
  }, []);

  const value = useMemo(
    () => ({
      mode,
      switchToDarkMode,
      switchToLightMode,
      switchToAutoMode,
    }),
    [mode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode(): ThemeModeContextValue {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeModeProvider");
  }
  return context;
}